/**
 * RoundTabs — tabs horizontales de navegación round-scoped.
 *
 * Reemplaza los links deshabilitados del drawer. Se monta en la parte superior
 * de cada pantalla round-scoped (Tablero, Operaciones, Monitor, Exports) para
 * que el contexto de ronda sea siempre visible y la navegación sea local. El
 * Tablero incluye el explorador de expedientes (antes "Cola de revisión").
 *
 * Roles (jerarquía reviewer ⊂ operator ⊂ admin):
 *   - reviewer+  : Tablero
 *   - operator+  : Operaciones, Monitor, Exportaciones
 */

import { useNavigate } from "react-router-dom";
import { Box, Tabs, Tab } from "@mui/material";
import DashboardIcon from "@mui/icons-material/Dashboard";
import BuildIcon from "@mui/icons-material/Build";
import MonitorHeartIcon from "@mui/icons-material/MonitorHeart";
import DownloadIcon from "@mui/icons-material/Download";

import { useAuth } from "../auth/AuthProvider";
import { roleAtLeast } from "../auth/roles";
import type { Role } from "../api/client";

type TabDef = {
  label: string;
  path: string;
  icon: React.ReactNode;
  minRole: Role;
};

function buildTabs(roundId: string): TabDef[] {
  return [
    {
      label: "Tablero",
      path: `/rounds/${roundId}`,
      icon: <DashboardIcon fontSize="small" />,
      minRole: "reviewer",
    },
    {
      label: "Operaciones",
      path: `/rounds/${roundId}/operations`,
      icon: <BuildIcon fontSize="small" />,
      minRole: "operator",
    },
    {
      label: "Monitor",
      path: `/rounds/${roundId}/monitor`,
      icon: <MonitorHeartIcon fontSize="small" />,
      minRole: "operator",
    },
    {
      label: "Exportaciones",
      path: `/rounds/${roundId}/exports`,
      icon: <DownloadIcon fontSize="small" />,
      minRole: "operator",
    },
  ];
}

type Props = {
  roundId: string;
  /** Current path — determines the active tab. */
  currentPath: string;
};

export function RoundTabs({ roundId, currentPath }: Props) {
  const navigate = useNavigate();
  const { role } = useAuth();

  const tabs = buildTabs(roundId).filter((t) => roleAtLeast(role, t.minRole));

  // Match the active tab by exact path or prefix (for nested routes), choosing
  // the MOST SPECIFIC match. The "Tablero" tab's path is the round root, so a
  // plain `findIndex` matched it for every sub-route (e.g. `/…/monitor` starts
  // with `/…/`), leaving Tablero perpetually selected. Pick the longest matching
  // path so nested routes select their own tab.
  const activeIndex = tabs.reduce((best, t, i) => {
    const matches = currentPath === t.path || currentPath.startsWith(t.path + "/");
    if (!matches) return best;
    return best < 0 || t.path.length > tabs[best].path.length ? i : best;
  }, -1);
  const value = activeIndex >= 0 ? activeIndex : false;

  return (
    <Box sx={{ borderBottom: 1, borderColor: "divider", mb: 3 }}>
      <Tabs
        value={value}
        onChange={(_, idx: number) => navigate(tabs[idx].path)}
        variant="scrollable"
        scrollButtons="auto"
        aria-label="Navegación de ronda"
      >
        {tabs.map((t) => (
          <Tab
            key={t.path}
            label={t.label}
            icon={t.icon as React.ReactElement}
            iconPosition="start"
            sx={{ minHeight: 48, textTransform: "none" }}
          />
        ))}
      </Tabs>
    </Box>
  );
}
